import { Badge } from "@tql/ui-components-react/kendo/indicators";

import type { StopSection } from "~/shared/schemas/shipment-details-schema.ts";

import styles from "./drawer-shipment-details.module.css";
import { getLocation, isValidTimestamp } from "./drawer-shipment-details.tsx";
import { EM_DASH, emDashIfBlank } from "./em-dash-if-blank.ts";
import { formatAppointmentWindow } from "./format-appointment-window.ts";
import { formatLocalShortDate } from "./format-ltl-shipment-details.ts";
import { formatScheduledShortDate } from "./format-scheduled-short-date.ts";
import type { OrderedStop } from "./merge-stops.ts";
import { mergeStops } from "./merge-stops.ts";

/**
 * Props for the unified non-LTL Shipment Details card. Single-stop loads
 * (1 pickup + 1 delivery) and multi-stop loads both render through this
 * component — there is no per-case branch (FR-021).
 */
export type NonLtlShipmentDetailsCardProps = {
	readonly pickups: readonly StopSection[];
	readonly deliveries: readonly StopSection[];
};

type StopType = "pickup" | "delivery";

/** Returns trimmed value, or null for null/undefined/empty/whitespace-only inputs. */
function blankToNull(value: string | null | undefined): string | null {
	if (!value) return null;
	const trimmed = value.trim();
	return trimmed.length === 0 ? null : trimmed;
}

type BadgeText = "Loaded" | "Not Loaded" | "Delivered" | "Not Delivered";

/** FR-013 four-state status badge machine. */
function getBadgeState(stop: StopSection): {
	readonly text: BadgeText;
	readonly themeColor: "success" | "error";
} {
	const isComplete = isValidTimestamp(stop.actualTimestamp);

	if (stop.stopType === "Pickup") {
		return isComplete
			? { text: "Loaded", themeColor: "success" }
			: { text: "Not Loaded", themeColor: "error" };
	}

	return isComplete
		? { text: "Delivered", themeColor: "success" }
		: { text: "Not Delivered", themeColor: "error" };
}

/**
 * Picks the horizontal-gap utility class for the section header so the
 * status badge sits the same visual distance from the title regardless of
 * which of the four badge labels is rendered. Tighter labels (`Loaded`,
 * `Delivered`) use a smaller gap because their pill is narrower.
 */
function getHeaderGapClassName(badgeText: BadgeText): string {
	switch (badgeText) {
		case "Loaded": {
			return "k-d-flex k-gap-10";
		}
		case "Delivered": {
			return "k-d-flex k-gap-12";
		}
		case "Not Delivered": {
			return "k-d-flex k-gap-16";
		}
		default: {
			return "k-d-flex k-gap-15";
		}
	}
}

function getReferenceLabel(
	type: StopType,
): "Pickup Number:" | "Delivery Number:" {
	return type === "pickup" ? "Pickup Number:" : "Delivery Number:";
}

/** Renders the address column. When ALL address sub-lines are blank, renders a single em-dash placeholder beneath the heading (FR-013 / FR-014). */
function renderAddressColumn(stop: StopSection, sequenceLabel: string) {
	const facilityName = blankToNull(stop.facilityName);
	const address1 = blankToNull(stop.address1);
	const address2 = blankToNull(stop.address2);
	const cityStateZip = getLocation(stop.city, stop.state, stop.postalCode);
	const country = blankToNull(stop.country);
	const cityStateZipCountry =
		cityStateZip && country
			? `${cityStateZip}, ${country}`
			: (cityStateZip ?? country);

	const type: StopType = stop.stopType === "Pickup" ? "pickup" : "delivery";

	const hasAnyAddressLine = Boolean(
		facilityName || address1 || address2 || cityStateZipCountry,
	);

	if (!hasAnyAddressLine) {
		return (
			<div className={styles.cardColumn}>
				<p className={styles.cardColumnLabel}>Address</p>
				<p
					className={styles.cardColumnValue}
					data-testid={`shipment-${type}-${sequenceLabel}-address-empty`}
				>
					{EM_DASH}
				</p>
			</div>
		);
	}

	return (
		<div className={styles.cardColumn}>
			<p className={styles.cardColumnLabel}>Address</p>
			{facilityName ? (
				<p
					className={styles.cardColumnValue}
					data-testid={`shipment-${type}-${sequenceLabel}-shipper-name`}
				>
					{facilityName}
				</p>
			) : null}
			{address1 ? <p className={styles.cardColumnValue}>{address1}</p> : null}
			{address2 ? <p className={styles.cardColumnValue}>{address2}</p> : null}
			{cityStateZipCountry ? (
				<p className={styles.cardColumnValue}>{cityStateZipCountry}</p>
			) : null}
		</div>
	);
}

type DetailRow = {
	readonly label: string;
	readonly value: string;
	readonly testId: string;
};

/**
 * Computes the visible right-column detail rows per FR-022 / FR-027 / FR-023.
 * Returns an empty array when no row would render (right column is omitted
 * by the caller per FR-028).
 */
function getDetailRows(
	stop: StopSection,
	type: StopType,
	sequenceLabel: string,
): readonly DetailRow[] {
	const isComplete = isValidTimestamp(stop.actualTimestamp);

	// When loaded/delivered, show the actual event timestamp in local timezone.
	// Before that event, fall back to the scheduled calendar date.
	let dateLabel: string;
	if (isComplete) {
		dateLabel = type === "pickup" ? "Picked Up On:" : "Delivered On:";
	} else {
		dateLabel = type === "pickup" ? "Estimated Pickup:" : "Estimated Delivery:";
	}

	const dateValue = isComplete
		? emDashIfBlank(formatLocalShortDate(stop.actualTimestamp))
		: emDashIfBlank(formatScheduledShortDate(stop.scheduledDate));

	// Render the appointment row to mirror the time-window portion of what
	// Freight Core's `StopTimeParser.FormatStopTime` writes (e.g. `FCFS 08:00 to 15:00`,
	// `Appt 14:30`). When the time type is `Unknown` or the start time is missing/
	// unparseable, the formatter returns `TBD`, which we map to em-dash per FR-015.
	const apptRaw = formatAppointmentWindow(
		stop.timeType,
		stop.appointmentStartTime,
		stop.appointmentEndTime,
	);
	const apptValue = apptRaw === "TBD" ? EM_DASH : apptRaw;

	const rows: readonly DetailRow[] = [
		{
			label: dateLabel,
			value: dateValue,
			testId: `shipment-${type}-${sequenceLabel}-date-row`,
		},
		{
			label: "Appointment:",
			value: apptValue,
			testId: `shipment-${type}-${sequenceLabel}-appointment-time`,
		},
		{
			label: getReferenceLabel(type),
			value: emDashIfBlank(stop.referenceNumber),
			testId: `shipment-${type}-${sequenceLabel}-reference-number`,
		},
	];

	return rows;
}

function renderDetailRows(rows: readonly DetailRow[]): React.ReactNode {
	return (
		<div className={styles.cardDetailRows}>
			{rows.map((row) => (
				<p key={row.label} className={styles.cardDetailRow}>
					<span className={styles.cardDetailRowLabel}>{row.label}</span>{" "}
					<span data-testid={row.testId}>{row.value}</span>
				</p>
			))}
		</div>
	);
}

function renderSection(entry: OrderedStop) {
	const { stop, type } = entry;
	const sequenceLabel = String(stop.sequence);
	const sequenceKey = `${type}-${sequenceLabel}`;
	const title =
		type === "pickup" ? `Pickup ${sequenceLabel}` : `Delivery ${sequenceLabel}`;

	const badge = getBadgeState(stop);
	const detailRows = getDetailRows(stop, type, sequenceLabel);

	return (
		<section
			key={sequenceKey}
			className={styles.cardSection}
			data-testid={`shipment-${type}-${sequenceLabel}-section`}
		>
			<div className={getHeaderGapClassName(badge.text)}>
				<h4 style={{ margin: 0, fontWeight: "normal" }}>{title}</h4>
				<span data-testid={`shipment-${type}-${sequenceLabel}-status-badge`}>
					<Badge
						align={{ horizontal: "start", vertical: "top" }}
						className={styles.cardStatusBadge}
						fillMode={"outline"}
						rounded={"full"}
						size={"medium"}
						style={{
							color:
								badge.themeColor === "success"
									? undefined
									: "var(--kendo-color-on-app-surface)",
						}}
						themeColor={badge.themeColor}
					>
						{badge.text}
					</Badge>
				</span>
			</div>
			<div className={styles.cardGrid}>
				{renderAddressColumn(stop, sequenceLabel)}
				{renderDetailRows(detailRows)}
			</div>
		</section>
	);
}

/**
 * Unified non-LTL Shipment Details card (Amendment 2026-05-01). Renders one
 * compact section per merged stop using `mergeStops` for ordering. Used by
 * single-stop and multi-stop loads alike — there is no rate-type branch
 * inside the card itself (FR-021); rate-type dispatch happens in the parent
 * `DrawerShipmentDetails` wrapper which keeps LTL/LTL-Partial on the legacy
 * layout.
 */
export function NonLtlShipmentDetailsCard({
	pickups,
	deliveries,
}: Readonly<NonLtlShipmentDetailsCardProps>) {
	const ordered = mergeStops({ pickups, deliveries });

	if (ordered.length === 0) {
		return null;
	}

	return (
		<div className={styles.cardSectionList} data-testid={"shipment-details"}>
			{ordered.map((entry) => renderSection(entry))}
		</div>
	);
}
